import { SolidifyableData } from "@lib/titan2/dist/api/core/data/SolidifyableData";
import { Dictionary } from "@lib/titan2/dist/util/collection/Dictionary";
import { SlingoConstants } from "../enum/SlingoConstants";
import { SymbolType } from "../enum/SymbolType";
import { ReelSequenceData } from "./ReelSequenceData";
import { StakeToSpinData } from "./StakeToSpin";
import { SymbolWinData } from "./SymbolWinData";
/*
 * SpinResultData.ts
 */
export class SpinResultData extends SolidifyableData {
    constructor(data) {
        super(data);
        this.solidify();
    }
    solidify() {
        super.solidify();
        if (this.dataExists(this.data)) {
            this.matchedNumbers = [];
            let matchedNumbers = this.data.getNumberArray(SlingoConstants.PROPERTY_MATCHED_NUMBERS);
            if (matchedNumbers) {
                this.matchedNumbers = matchedNumbers;
            }
            this.jokerCells = this.data.getNumberArray(SlingoConstants.PROPERTY_JOKER_CELLS);
            this.superJokerCells = this.data.getNumberArray(SlingoConstants.PROPERTY_SUPER_JOKER_CELLS);
            this.solidifyReelSequence();
            this.solidifySymbolWins();
            this.solidifyStakeToSpin();
            this.solidifyNextStakeToSpin();
            this.totalNextStakeToSpin = this.data.getNumber(SlingoConstants.PROPERTY_TOTAL_NEXT_STAKE_TO_SPIN);
            this.totalStakeToSpin = this.data.getNumber(SlingoConstants.PROPERTY_TOTAL_STAKE_TO_SPIN);
            this.solidifyJokerStored();
            this.solidifySuperJokerStored();
            this.solidifyPreviousJokerStored();
            this.solidifyPreviousSuperJokerStored();
            this.solidifyTotalJokerStored();
            this.solidifyTotalSuperJokerStored();
        }
    }
    solidifyReelSequence() {
        let reelSequenceData = this.data.getDataObject(SlingoConstants.PROPERTY_REEL_SEQUENCE);
        if (reelSequenceData) {
            this.reelSequence = new ReelSequenceData(reelSequenceData);
        }
    }
    solidifySymbolWins() {
        let symbolWinArray = this.data.getDataArray(SlingoConstants.PROPERTY_SYMBOL_WINS);
        this.symbolWins = new Dictionary();
        if (symbolWinArray) {
            for (let i = 0; i < symbolWinArray.length; i++) {
                let win = new SymbolWinData(symbolWinArray[i]);
                this.symbolWins.setValue(win.getType(), win);
            }
        }
    }
    solidifyStakeToSpin() {
        this.stakeToSpin = new StakeToSpinData(this.data.getDataObject(SlingoConstants.PROPERTY_STAKE_TO_SPIN));
    }
    solidifyNextStakeToSpin() {
        let nextStakeToSpinArray = this.data.getDataArray(SlingoConstants.PROPERTY_NEXT_STAKE_TO_SPIN);
        if (nextStakeToSpinArray && nextStakeToSpinArray.length) {
            this.nextStakeToSpin = nextStakeToSpinArray.map((data) => new StakeToSpinData(data));
        }
        else {
            this.nextStakeToSpin = [];
        }
    }
    solidifyJokerStored() {
        const feature = this.data?.getDataObject(SlingoConstants.PROPERTY_FEATURE);
        this.jokerStored = feature?.getNumberArray(SlingoConstants.PROPERTY_JOKER_STORED);
    }
    solidifySuperJokerStored() {
        const feature = this.data?.getDataObject(SlingoConstants.PROPERTY_FEATURE);
        this.superJokerStored = feature?.getNumberArray(SlingoConstants.PROPERTY_SUPER_JOKER_STORED);
    }
    solidifyPreviousJokerStored() {
        const feature = this.data?.getDataObject(SlingoConstants.PROPERTY_FEATURE);
        this.previousJokerStored = feature?.getNumberArray(SlingoConstants.PROPERTY_PREVIOUS_JOKER_STORED);
    }
    solidifyPreviousSuperJokerStored() {
        const feature = this.data?.getDataObject(SlingoConstants.PROPERTY_FEATURE);
        this.previousSuperJokerStored = feature?.getNumberArray(SlingoConstants.PROPERTY_PREVIOUS_SUPER_JOKER_STORED);
    }
    solidifyTotalJokerStored() {
        const feature = this.data?.getDataObject(SlingoConstants.PROPERTY_FEATURE);
        this.totalJokerStored = feature?.getNumberArray(SlingoConstants.PROPERTY_TOTAL_JOKER_STORED);
    }
    solidifyTotalSuperJokerStored() {
        const feature = this.data?.getDataObject(SlingoConstants.PROPERTY_FEATURE);
        this.totalSuperJokerStored = feature?.getNumberArray(SlingoConstants.PROPERTY_TOTAL_SUPER_JOKER_STORED);
    }
    getMatchedNumbers() {
        return this.matchedNumbers?.slice();
    }
    getJokerCells() {
        return this.jokerCells?.slice();
    }
    getSuperJokerCells() {
        return this.superJokerCells?.slice();
    }
    getReelSequence() {
        return this.reelSequence;
    }
    getSymbolWins() {
        return this.symbolWins.getValues();
    }
    getCoinWin() {
        return this.symbolWins.getValue(SymbolType.COIN);
    }
    getJokerWin() {
        return this.symbolWins.getValue(SymbolType.JOKER);
    }
    hasMatchedNumber() {
        return this.matchedNumbers.length > 0;
    }
    hasSymbolWin() {
        return this.symbolWins.size() > 0;
    }
    hasCoinWin() {
        return this.symbolWins.containsKey(SymbolType.COIN);
    }
    hasJokerWin() {
        return this.symbolWins.containsKey(SymbolType.JOKER);
    }
    /**
     * Gives a running total of cash spent on losing wheel spins this turn.
     * When you get a winning wheel spin, this resets to `0` and the value is added to `totalStakeToSpin`
     */
    getTotalNextStakeToSpin() {
        return this.totalNextStakeToSpin;
    }
    /**
     * Gives the total amount spent on wheel spins this game.
     */
    getTotalStakeToSpin() {
        return this.totalStakeToSpin;
    }
    /**
     * An array of all the losing wheel spins on the current slingo spin
     */
    getNextStakeToSpin() {
        return this.nextStakeToSpin.slice();
    }
    /**
     * The last winning wheel spin result
     */
    getStakeToSpin() {
        return this.stakeToSpin;
    }
    /**
     * @returns An array of joker stored indexes for express game
     */
    getJokerStored() {
        return this.jokerStored;
    }
    /**
     * @returns Number of super joker stored for express game
     */
    getSuperJokerStored() {
        return this.superJokerStored;
    }
    /**
     * @returns An array of previous joker stored indexes for express game
     */
    getPreviousJokerStored() {
        return this.previousJokerStored;
    }
    /**
     * @returns Number of previous super joker stored for express game
     */
    getPreviousSuperJokerStored() {
        return this.previousSuperJokerStored;
    }
    /**
     * @returns An array of total joker stored indexes for express game
     */
    getTotalJokerStored() {
        return this.totalJokerStored;
    }
    /**
     * @returns Number of total super joker stored for express game
     */
    getTotalSuperJokerStored() {
        return this.totalSuperJokerStored;
    }
}
//# sourceMappingURL=SpinResultData.js.map